import React, { useState } from "react";
import { Table, Collapse, Button } from "reactstrap";
import PropTypes from "prop-types";
import formatCurrency from "constants/formatCurrency";
import formatDate from "constants/formatDate";

const EventsIncomeReportTable = ({ locations }) => {
    const [expanded, setExpanded] = useState({});
    const [page, setPage] = useState({});
    const itemsPerPage = 5;

    const toggleExpand = (key) => {
        setExpanded((prev) => ({ ...prev, [key]: !prev[key] }));
    };

    const paginate = (arr, key) => {
        const currentPage = page[key] || 1;
        const start = (currentPage - 1) * itemsPerPage;
        return arr.slice(start, start + itemsPerPage);
    };

    const totalPages = (arr) => Math.max(1, Math.ceil(arr.length / itemsPerPage));

    return (
        <div className="overflow-x-auto">
            <Table bordered responsive className="align-middle table-nowrap">
                <thead className="table-light">
                    <tr>
                        <th>Location</th>
                        <th>Income</th>
                        <th></th>
                    </tr>
                </thead>
                <tbody>
                    {locations
                        .slice()
                        .sort((a, b) => a.name.localeCompare(b.name))
                        .filter((item) => item.events.length > 0)
                        .map((loc) => {
                            const totalIncome = loc.events.reduce(
                                (acc, e) => acc + (e.invoice || 0),
                                0
                            );
                            const key = `loc-${loc.id || "general"}`;

                            return (
                                <React.Fragment key={key}>
                                    <tr>
                                        <td>{loc.name}</td>
                                        <td>${formatCurrency(totalIncome)}</td>
                                        <td>
                                            <Button
                                                className="button-gradient-bg"
                                                onClick={() => toggleExpand(key)}
                                            >
                                                {expanded[key] ? "Hide Events" : "View Events"}
                                            </Button>
                                        </td>
                                    </tr>
                                    <tr>
                                        <td colSpan={3} className="p-0 border-0">
                                            <Collapse isOpen={expanded[key]}>
                                                <Table
                                                    bordered
                                                    size="sm"
                                                    responsive
                                                    className="mb-0 table-striped"
                                                >
                                                    <thead>
                                                        <tr>
                                                            <th>Date</th>
                                                            <th>Name</th>
                                                            <th>Income</th>
                                                            <th>Labor Cost</th>
                                                            <th>Non Labor Cost</th>
                                                        </tr>
                                                    </thead>
                                                    <tbody>
                                                        {paginate(loc.events, key).map((e) => (
                                                            <tr key={e.id}>
                                                                <td>{formatDate(e.event_date)}</td>
                                                                <td>{e.event_name}</td>
                                                                <td>${formatCurrency(e.invoice)}</td>
                                                                <td>${formatCurrency(e.labor_cost)}</td>
                                                                <td>${formatCurrency(e.non_labor)}</td>
                                                            </tr>
                                                        ))}
                                                    </tbody>
                                                </Table>
                                                {loc.events.length > itemsPerPage && (
                                                    <div className="d-flex justify-content-between align-items-center p-2">
                                                        <Button
                                                            color="secondary"
                                                            size="sm"
                                                            onClick={() =>
                                                                setPage((prev) => ({
                                                                    ...prev,
                                                                    [key]: Math.max(
                                                                        1,
                                                                        (page[key] || 1) - 1
                                                                    ),
                                                                }))
                                                            }
                                                            disabled={(page[key] || 1) === 1}
                                                        >
                                                            Previous
                                                        </Button>
                                                        <span>
                                                            Page {page[key] || 1} of{" "}
                                                            {totalPages(loc.events)}
                                                        </span>
                                                        <Button
                                                            color="secondary"
                                                            size="sm"
                                                            onClick={() =>
                                                                setPage((prev) => ({
                                                                    ...prev,
                                                                    [key]: Math.min(
                                                                        totalPages(loc.events),
                                                                        (page[key] || 1) + 1
                                                                    ),
                                                                }))
                                                            }
                                                            disabled={
                                                                (page[key] || 1) ===
                                                                totalPages(loc.events)
                                                            }
                                                        >
                                                            Next
                                                        </Button>
                                                    </div>
                                                )}
                                            </Collapse>
                                        </td>
                                    </tr>
                                </React.Fragment>
                            );
                        })}
                    {locations.length === 0 && (
                        <tr>
                            <td colSpan={3} className="text-center py-3">
                                No locations found
                            </td>
                        </tr>
                    )}
                </tbody>
            </Table>
        </div>
    );
};

EventsIncomeReportTable.propTypes = {
    locations: PropTypes.arrayOf(
        PropTypes.shape({
            id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
            name: PropTypes.string.isRequired,
            events: PropTypes.arrayOf(
                PropTypes.shape({
                    id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
                    event_date: PropTypes.string.isRequired,
                    invoice: PropTypes.number.isRequired,
                    labor_cost: PropTypes.number.isRequired,
                    non_labor: PropTypes.number.isRequired,
                })
            ),
        })
    ).isRequired,
};

export default EventsIncomeReportTable;